import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, Copy, Search, AlertCircle } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { format, subDays, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { DayPicker } from "react-day-picker";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useState, useEffect } from "react";
import { verifyAddress } from "@/data-access/blockchain";

const searchSchema = z.object({
  query: z.string().min(1, "Search query is required"),
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
});

type SearchValues = z.infer<typeof searchSchema>;

interface SearchUpdateValues extends SearchValues {
  selectedRange?: string | null;
  isManualDate?: boolean;
}

interface DefaultValues extends SearchValues {
  selectedRange?: string | null;
  hasManualDates?: boolean;
}

interface WalletAddressSearchBarProps {
  onUpdate?: (values: SearchUpdateValues) => void;
  defaultValues?: DefaultValues;
}

export default function WalletAddressSearchBar({ onUpdate, defaultValues }: WalletAddressSearchBarProps) {
  // Determine initial selectedDateRange based on defaultValues
  const getInitialDateRange = (): string => {
    // If we have a range parameter from URL, use that
    if (defaultValues?.selectedRange) {
      // Convert URL range format to component format
      switch (defaultValues.selectedRange) {
        case 'last_week':
          return 'last-week';
        case 'last_month':
          return 'last-month';
        case 'last_3_months':
          return 'last-3-months';
        default:
          return 'all-transactions';
      }
    }
    
    // If we have manual dates in URL, don't preselect any option
    if (defaultValues?.hasManualDates) {
      return '';
    }
    
    // If both dates are null, it means "All Transactions" was selected
    if (defaultValues?.startDate === null && defaultValues?.endDate === null) {
      return "all-transactions";
    }
    
    // Otherwise no preselection
    return '';
  };

  const [selectedDateRange, setSelectedDateRange] = useState<string>(getInitialDateRange());
  const [isManualDateChange, setIsManualDateChange] = useState(false);
  const [startDateMonth, setStartDateMonth] = useState<Date | undefined>(undefined);
  const [endDateMonth, setEndDateMonth] = useState<Date | undefined>(undefined);

  const form = useForm<SearchValues>({
    resolver: zodResolver(searchSchema),
    defaultValues: defaultValues || {
      query: "",
      startDate: subDays(new Date(), 7),
      endDate: new Date(),
    },
  });
  const [copied, setCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [addressVerificationError, setAddressVerificationError] = useState<string | null>(null);

  // Initialize month states based on form values
  useEffect(() => {
    const startDate = form.getValues("startDate");
    const endDate = form.getValues("endDate");
    
    if (startDate) {
      setStartDateMonth(startDate);
    }
    if (endDate) {
      setEndDateMonth(endDate);
    }
  }, []);

  // Update selectedDateRange when defaultValues change
  useEffect(() => {
    const newDateRange = getInitialDateRange();
    setSelectedDateRange(newDateRange);
  }, [defaultValues?.startDate, defaultValues?.endDate]);

  // Clear address verification error when query changes
  useEffect(() => {
    const subscription = form.watch((data) => {
      if (!data.query || data.query.trim().length === 0) {
        setAddressVerificationError(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const handleCopy = () => {
    const value = form.getValues("query");
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1000);
  };

  const onSubmit = async (data: SearchValues) => {
    setIsSubmitting(true);
    setAddressVerificationError(null);
    try {
      // Verify the address before proceeding with the search
      const trimmedAddress = data.query.trim();
      if (trimmedAddress.length >= 26) {
        try {
          await verifyAddress(trimmedAddress);
        } catch (error) {
          setAddressVerificationError(
            error instanceof Error ? error.message : "The provided address is not a valid Bitcoin address. Please check the address and try again."
          );
          return;
        }
      }

      // Convert selectedDateRange format to URL format
      const urlRange = selectedDateRange === 'last-week' ? 'last_week' :
                       selectedDateRange === 'last-month' ? 'last_month' :
                       selectedDateRange === 'last-3-months' ? 'last_3_months' :
                       null;

      onUpdate?.({
        ...data,
        selectedRange: urlRange,
        isManualDate: isManualDateChange
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const startDate = form.watch("startDate");
  const endDate = form.watch("endDate");
  
  return (
    <form
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4 bg-card border border-border rounded-xl p-4 shadow-sm w-full"
    >
      <div className="flex gap-6">
        {/* Search Type */}
        <div className="flex flex-col w-32">
          <span className="text-xs text-muted-foreground mb-2">Search Type</span>
          <span className="font-medium text-card-foreground">Wallet Address</span>
        </div>
        {/* Search Query */}
        <div className="flex flex-col flex-1 min-w-[260px]">
          <span className="text-xs text-muted-foreground mb-2">Search Query</span>
          <div className="relative flex items-center">
            <Input
              {...form.register("query")}
              className="pr-10 h-9 bg-background border-input"
              placeholder="Enter wallet address"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-primary hover:text-primary/80 transition-colors"
              onClick={handleCopy}
              tabIndex={-1}
            >
              {copied ? (
                <span className="text-xs font-medium text-success">Copied!</span>
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>
          {addressVerificationError && (
            <div className="flex items-center gap-2 mt-2 text-sm" style={{ color: '#f59e0b' }}>
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{addressVerificationError}</span>
            </div>
          )}
        </div>
        {/* Date Selectors and Quick Select Container */}
        <div className="flex flex-col gap-4">
          {/* Date Inputs - Hidden when "All Transactions" is selected */}
          {selectedDateRange !== "all-transactions" && (
            <div className="flex gap-6">
              {/* Start Date */}
              <div className="flex flex-col w-40">
                <span className="text-xs text-muted-foreground mb-2">Start Date</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="relative w-full bg-background border border-input h-9 rounded-lg text-left flex items-center px-3 hover:bg-accent transition-colors"
                    >
                      <span className={form.watch("startDate") ? "text-foreground" : "text-muted-foreground"}>
                        {form.watch("startDate") ? format(form.watch("startDate")!, "yyyy/MM/dd") : "Pick a date"}
                      </span>
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary pointer-events-none">
                        <CalendarIcon className="h-4 w-4" />
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="p-2 w-auto bg-popover border border-border shadow-md">
                    <DayPicker
                      mode="single"
                      captionLayout="dropdown"
                      selected={form.watch("startDate") ?? undefined}
                      month={startDateMonth ?? form.watch("startDate") ?? undefined}
                      onMonthChange={setStartDateMonth}
                      onSelect={(date) => {
                        form.setValue("startDate", date ?? null);
                        setIsManualDateChange(true);
                        setSelectedDateRange(''); // Clear radio selection when manually changing dates
                        if (date) {
                          setStartDateMonth(date);
                        }
                      }}
                      className="text-popover-foreground"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {/* End Date */}
              <div className="flex flex-col w-40">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground mb-1">End Date</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    className="h-5 p-2 mb-1"
                    onClick={() => {
                      const today = new Date();
                      form.setValue("endDate", today);
                      setIsManualDateChange(true);
                      setSelectedDateRange(''); // Clear radio selection when manually changing dates
                      setEndDateMonth(today); // Update calendar to show current month
                    }}
                  >
                    Today
                  </Button>
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="relative w-full bg-background border border-input h-9 rounded-lg text-left flex items-center px-3 hover:bg-accent transition-colors"
                    >
                      <span className={form.watch("endDate") ? "text-foreground" : "text-muted-foreground"}>
                        {form.watch("endDate") ? format(form.watch("endDate")!, "yyyy/MM/dd") : "Pick a date"}
                      </span>
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-primary pointer-events-none">
                        <CalendarIcon className="h-4 w-4" />
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="p-2 w-auto bg-popover border border-border shadow-md">
                    <DayPicker
                      mode="single"
                      captionLayout="dropdown"
                      selected={form.watch("endDate") ?? undefined}
                      month={endDateMonth ?? form.watch("endDate") ?? undefined}
                      onMonthChange={setEndDateMonth}
                      onSelect={(date) => {
                        form.setValue("endDate", date ?? null);
                        setIsManualDateChange(true);
                        setSelectedDateRange(''); // Clear radio selection when manually changing dates
                        if (date) {
                          setEndDateMonth(date);
                        }
                      }}
                      className="text-popover-foreground"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}
          
          {/* Update Button positioned after date inputs or quick select */}
          <div className="flex items-center justify-between gap-4">
            {/* Quick Select */}
            <RadioGroup
              value={selectedDateRange}
              onValueChange={(value) => {
                setSelectedDateRange(value);
                setIsManualDateChange(false); // Clear manual date flag when using preset
                const today = new Date();
                switch (value) {
                  case "last-week":
                    const startWeek = subDays(today, 7);
                    form.setValue("startDate", startWeek);
                    form.setValue("endDate", today);
                    setStartDateMonth(startWeek);
                    setEndDateMonth(today);
                    break;
                  case "last-month":
                    const startMonth = startOfMonth(subMonths(today, 1));
                    const endMonth = endOfMonth(subMonths(today, 1));
                    form.setValue("startDate", startMonth);
                    form.setValue("endDate", endMonth);
                    setStartDateMonth(startMonth);
                    setEndDateMonth(endMonth);
                    break;
                  case "last-3-months":
                    const start3Months = startOfMonth(subMonths(today, 3));
                    const end3Months = endOfMonth(subMonths(today, 1));
                    form.setValue("startDate", start3Months);
                    form.setValue("endDate", end3Months);
                    setStartDateMonth(start3Months);
                    setEndDateMonth(end3Months);
                    break;
                  case "all-transactions":
                    form.setValue("startDate", null);
                    form.setValue("endDate", null);
                    setStartDateMonth(undefined);
                    setEndDateMonth(undefined);
                    break;
                }
              }}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="last-week" id="last-week" />
                <label htmlFor="last-week" className="text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
                  Last week
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="last-month" id="last-month" />
                <label htmlFor="last-month" className="text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
                  Last month
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="last-3-months" id="last-3-months" />
                <label htmlFor="last-3-months" className="text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
                  Last 3 months
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all-transactions" id="all-transactions" />
                <label htmlFor="all-transactions" className="text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
                  All Transactions
                </label>
              </div>
            </RadioGroup>
            
            {/* Update Button */}
            <Button
              type="submit"
              variant="outline"
              disabled={isSubmitting}
              size="sm"
            >
              <Search className="w-4 h-4 mr-1" /> 
              {isSubmitting ? "Updating..." : "Update"}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
} 